import { Check, Flag, X } from "lucide-react";
import styles from "./questionList.module.scss";

// ---------------------------------------------------------------------------
// QuestionList
// Sidebar list of question numbers, used by the test page and the completed
// test review. Adapted from the question-set app: our tests are one flat list
// of question ids, so there are no sets or groups here.
//
// Props
//   questionIds     number[]                       – questions in test order
//   currentIndex    number                         – highlighted row
//   isAnswered      (id) => boolean                – hides the "unanswered" dot
//   isMarked        (id) => boolean                – shows the flag
//   getResult       (id) => boolean | undefined    – review only: ✓ / ✗ icon
//   onSelect        (index) => void
//   height          number | string                – matches the center columns
// ---------------------------------------------------------------------------
type QuestionListProps = {
  questionIds: (number | string)[];
  currentIndex: number;
  isAnswered: (id: number | string) => boolean;
  isMarked: (id: number | string) => boolean;
  getResult?: (id: number | string) => boolean | undefined;
  onSelect: (index: number) => void;
  height?: number | string;
};

const QuestionList = ({
  questionIds,
  currentIndex,
  isAnswered,
  isMarked,
  getResult,
  onSelect,
  height,
}: QuestionListProps) => {
  return (
    <nav
      className={styles.questionsList}
      style={{ height }}
      aria-label="Questions"
    >
      <ul>
        {questionIds.map((questionId, i) => {
          const className = [styles.questionListItem];
          if (i === currentIndex) className.push(styles.current);
          if (isAnswered(questionId)) className.push(styles.completedListItem);
          const result = getResult?.(questionId);

          return (
            <li
              key={`${questionId}-${i}`}
              className={className.join(" ")}
              onClick={() => onSelect(i)}
              aria-current={i === currentIndex ? "true" : undefined}
            >
              <p>{i + 1}</p>
              {result === true && (
                <Check className={styles.resultCorrect} aria-label="Correct" />
              )}
              {result === false && (
                <X className={styles.resultIncorrect} aria-label="Incorrect" />
              )}
              {isMarked(questionId) && (
                <Flag className={styles.listMarkedFlag} aria-label="Marked" />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default QuestionList;
